import React, { useEffect, useState } from 'react'
import {
	Grid,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Box,
	Paper,
	Typography,
	Button,
	CircularProgress,
	useTheme,
	alpha,
	Avatar,
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import ContactlessIcon from '@mui/icons-material/Contactless'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { DirectReportAttendance } from '../../../types/Employee/Attendance'
import {
    cardSignOut,
	getDirectReportsAttendance,
} from '../../../services/attendanceService'
import { useNFCReader } from '../../hooks/useNFCReader'
import ConsentDialog from './ConsentDialog'
import { useProjectFeaturesContext } from '../../project/contexts/ProjectFeaturesContext'
import { useConsentContext } from './ConsentContext'
import { AttendanceLayoutVariant, attendanceCardBodySx, attendanceCardGridSx, attendanceCardPanelHeightSx } from './attendanceLayout'
import { AttendanceBody, AttendanceShell } from './AttendanceShell'

export type CardSignOutDialogProps = {
	supervisorId?: number
	department: string
	open: boolean
	variant?: AttendanceLayoutVariant
}

export const CardSignOutDialog = ({
	supervisorId,
	department,
	open,
	variant = 'dialog',
}: CardSignOutDialogProps): JSX.Element => {
	const theme = useTheme()
	const [attendances, setAttendances] = useState<DirectReportAttendance[]>([])
	const [loadingAttendances, setLoadingAttendances] = useState(false)
	const [scannedWorker, setScannedWorker] = useState<DirectReportAttendance | null>(null)
	const [loadingSignOut, setLoadingSignOut] = useState(false)
	const { nfcMessage, error, startReading, stopReading, setNfcMessage } = useNFCReader()
	const {isFeatureEnabled} = useProjectFeaturesContext()
	const {getSignature, loadConsentTexts, generateConsentForms, clearSignatures} = useConsentContext()
	const isConsentEnabled = isFeatureEnabled(6)
	const loadAttendances = async () => {
		const data = await getDirectReportsAttendance(department, supervisorId, true, null)
		setAttendances(data.filter((w) => w.signOutAt === null))
	}

	useEffect(() => {
		if (open) {
			;(async () => {
				setLoadingAttendances(true)
				try {
					await Promise.all([loadAttendances(), loadConsentTexts()])
				} finally {
					setLoadingAttendances(false)
				}
				startReading()
			})()
		} else {
			setScannedWorker(null)
			stopReading()
		}
		return () => {
			stopReading()
			clearSignatures()

		}
	}, [open, department, supervisorId, startReading, stopReading])

	useEffect(() => {
		if (nfcMessage) {
			const worker = attendances.find((a) => a.cardId === nfcMessage.cardId)
			if (!worker) {
				alert('Worker not found')
				return
			}
			setScannedWorker(worker)
		}
	}, [nfcMessage, attendances])

	const handleConfirm = async () => {
		if (!scannedWorker || !nfcMessage) return
		if(isConsentEnabled && !getSignature(scannedWorker.workerId)) {
			setScannedWorker((prev) => (prev ? { ...prev, consentError: true } : prev))
			return
		}
		setLoadingSignOut(true)
		const forms = isConsentEnabled
			? generateConsentForms(
					[scannedWorker.workerId],
			  )
			: []
		try {
			await cardSignOut({
				cardId: nfcMessage?.cardId,
				cardToken: nfcMessage?.token,
				areaId: undefined,
			}, forms)
			await loadAttendances()
		} catch (error) {
			console.error('Error signing out', error)
		} finally {
			setNfcMessage(null)
			setLoadingSignOut(false)
			setScannedWorker(null)
		}
	}
	function handleConsentChange(): void {
		setScannedWorker((prev) => (prev ? { ...prev, consentError: false } : null))
	}
	return (
		<AttendanceShell variant={variant}>
			<AttendanceBody variant={variant} sx={attendanceCardBodySx(theme, variant)}>
				<Grid container spacing={3} sx={attendanceCardGridSx(variant)}>
				<Grid item xs={5} sx={variant === 'page' ? { display: 'flex', minHeight: 0 } : undefined}>
					<Paper
						variant='outlined'
						sx={{
							...attendanceCardPanelHeightSx(variant),
							overflowY: 'auto',
							background: alpha(theme.palette.grey[50], 0.8),
							borderRadius: 2,
							boxShadow: theme.shadows[1],
							border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
						}}
					>
						{loadingAttendances ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
								<CircularProgress size={40} />
							</Box>
						) : (
							<List>
								{attendances.map((att) => (
									<ListItem
										key={att.workerId}
										selected={scannedWorker?.workerId === att.workerId}
										sx={{
											width: '100%',
											px: 2,
											py: 1.5,
											borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
											'&:last-child': {
												borderBottom: 'none',
											},
											'&:hover': {
												backgroundColor: alpha(theme.palette.primary.main, 0.04),
											},
											'&.Mui-selected': {
												backgroundColor: alpha(theme.palette.primary.main, 0.08),
												'&:hover': {
													backgroundColor: alpha(theme.palette.primary.main, 0.12),
												},
											},
											gap: 2,
										}}
									>
										<ListItemIcon>
											<Avatar
												sx={{
													bgcolor: theme.palette.primary.main,
													width: 40,
													height: 40,
												}}
											>
												<PersonIcon />
											</Avatar>
										</ListItemIcon>
										<ListItemText
											primary={
												<Typography variant='subtitle1' fontWeight='medium'>
													{att.fullName}
												</Typography>
											}
										/>
									</ListItem>
								))}
							</List>
						)}
					</Paper>
				</Grid>
				<Grid item xs={7} sx={variant === 'page' ? { display: 'flex', minHeight: 0 } : undefined}>
					<Paper
						variant='outlined'
						sx={{
							...attendanceCardPanelHeightSx(variant),
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							p: 3,
							background: alpha(theme.palette.grey[50], 0.8),
							borderRadius: 2,
							boxShadow: theme.shadows[1],
							border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
						}}
					>
						{!scannedWorker ? (
							<Box
								sx={{
									border: `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
									borderRadius: '12px',
									width: '100%',
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									gap: 2,
									background: alpha(theme.palette.primary.main, 0.02),
									'&:hover': {
										borderColor: theme.palette.primary.main,
										background: alpha(theme.palette.primary.main, 0.04),
									},
								}}
							>
								<ContactlessIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7 }} />
								<Typography
									variant='h5'
									color='primary'
									fontWeight='medium'
									sx={{ textAlign: 'center', maxWidth: '80%' }}
								>
									Waiting for NFC card...
								</Typography>
								{error && (
									<Box
										sx={{
											mt: 1,
											p: 1.5,
											background: alpha(theme.palette.error.main, 0.1),
											borderRadius: 1,
											border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
										}}
									>
										<Typography color='error' variant='body2' sx={{ fontWeight: 'medium' }}>
											{error}
										</Typography>
									</Box>
								)}
							</Box>
						) : (
							<Box
								sx={{
									width: '100%',
									textAlign: 'center',
									display: 'flex',
									flexDirection: 'column',
									gap: 3,
									alignItems: 'center',
								}}
							>
								<Avatar
									sx={{
										bgcolor: theme.palette.primary.main,
										width: 80,
										height: 80,
										mb: 2,
									}}
								>
									<PersonIcon sx={{ fontSize: 40 }} />
								</Avatar>
								<Typography
									variant='h5'
									fontWeight='medium'
									sx={{
										color: 'primary.main',
										textTransform: 'uppercase',
										letterSpacing: 1,
										mb: 1,
									}}
								>
									{scannedWorker.fullName}
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
									<LocationOnIcon color='action' />
									<Typography variant='body2' color='text.secondary'>
										Last Area: {scannedWorker.areaName || 'N/A'}
									</Typography>
								</Box>
								{isConsentEnabled && 
									<ConsentDialog
										type='dayEndPrompt'
										workerId={scannedWorker.workerId}
										error={scannedWorker.consentError}
										onConsentChange={handleConsentChange}
									/>
								}
								<Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
									{!loadingSignOut ? (
										<Button
											variant='contained'
											color='primary'
											onClick={handleConfirm}
											disabled={isConsentEnabled && !getSignature(scannedWorker.workerId)}
											sx={{
												minWidth: 150,
												py: 1.5,
												fontWeight: 'medium',
												boxShadow: theme.shadows[1],
												'&:hover': {
													boxShadow: theme.shadows[2],
												},
											}}
										>
											Confirm Sign Out
										</Button>
									) : (
										<CircularProgress size={32} sx={{ p: 0, m: 0 }} />
									)}
									<Button
										color='secondary'
										onClick={() => setScannedWorker(null)}
										sx={{
											color: 'primary.main',
											minWidth: 150,
											py: 1.5,
											fontWeight: 'medium',
											border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
											background: alpha(theme.palette.grey[100], 0.8),
											'&:hover': {
												background: alpha(theme.palette.grey[200], 0.8),
											},
										}}
									>
										Cancel
									</Button>
								</Box>
							</Box>
						)}
					</Paper>
				</Grid>
			</Grid>
			</AttendanceBody>
		</AttendanceShell>
	)
}
